/**
 * Per-product presentation data — hue, mark geometry, labels.
 *
 * Deliberately JSX-free (a `.ts`, not a `.tsx`): the marks are stored as SVG
 * element descriptors and rendered by `products.tsx`. Keeping data as data means
 * this file exports no components, which both satisfies fast refresh and puts
 * every bit of rendering in one place.
 *
 * **These marks are not logos.** No per-product logo exists in any of the source
 * repos — only the company logo and one product's own — and standing a company
 * logo in for a product would misrepresent the other four. They are neutral
 * geometric marks chosen to say what each product does, sized and placed exactly
 * where a real logo would go, so dropping real ones in later is a change to this
 * file alone.
 *
 * An unknown key degrades to a neutral grey and a generic mark rather than
 * throwing: a product an admin adds to the catalogue without touching the front
 * end must still render.
 */
import type { PipelineStage } from '../../api/schema';

/** Hue per product, from the theme's categorical tokens so both themes work. */
const HUES: Record<string, string> = {
  inteliedocs: 'var(--c1)',
  intelie_konnect: 'var(--c2)',
  intelie_kb: 'var(--c3)',
  easy_read: 'var(--c4)',
  plain_english: 'var(--c2)',
  contract_intelligence: 'var(--c5)',
};

const FALLBACK_HUE = 'var(--c6)';

export function productHue(key: string): string {
  return HUES[key] ?? FALLBACK_HUE;
}

/** One element of a mark, on a 16x16 grid, stroked in `currentColor`. */
export type MarkPart =
  | { kind: 'path'; d: string; width?: number; fill?: boolean; join?: boolean }
  | { kind: 'circle'; cx: number; cy: number; r: number; width?: number };

/**
 * Each mark says what the product does rather than decorating: a page with a
 * bracketed field for extraction, two joined nodes for a connector, stacked
 * layers for a knowledge base, one long line and one short for readable text, a
 * page of even full-width lines for a plain-English rewrite that drops nothing, a
 * page with a signature for contracts.
 */
export const MARKS: Record<string, MarkPart[]> = {
  inteliedocs: [
    { kind: 'path', d: 'M4 2.5h5.5L12 5v8.5H4z', width: 1.3, join: true },
    { kind: 'path', d: 'M9.3 2.6v2.6H12', width: 1.3, join: true },
    { kind: 'path', d: 'M6 9.6h4', width: 1.5 },
    { kind: 'path', d: 'M6 11.6h2.3', width: 1.5 },
  ],
  intelie_konnect: [
    { kind: 'circle', cx: 4, cy: 8, r: 2.1, width: 1.3 },
    { kind: 'circle', cx: 12, cy: 8, r: 2.1, width: 1.3 },
    { kind: 'path', d: 'M6.1 8h3.8', width: 1.5 },
  ],
  intelie_kb: [
    { kind: 'path', d: 'M8 2.4 13.4 5 8 7.6 2.6 5z', width: 1.3, join: true },
    { kind: 'path', d: 'M2.6 8.2 8 10.8l5.4-2.6', width: 1.3, join: true },
    { kind: 'path', d: 'M2.6 11.2 8 13.8l5.4-2.6', width: 1.3, join: true },
  ],
  easy_read: [
    { kind: 'path', d: 'M3 5.2h10', width: 1.9 },
    { kind: 'path', d: 'M3 8.4h10', width: 1.9 },
    { kind: 'path', d: 'M3 11.6h4.6', width: 1.9 },
  ],
  plain_english: [
    { kind: 'path', d: 'M4 2.5h8v11H4z', width: 1.3, join: true },
    { kind: 'path', d: 'M6 5.6h4M6 7.8h4M6 10h4', width: 1.3 },
  ],
  contract_intelligence: [
    { kind: 'path', d: 'M4 2.5h8v11H4z', width: 1.3, join: true },
    { kind: 'path', d: 'M6.2 5.6h3.6M6.2 7.8h3.6', width: 1.3 },
    { kind: 'path', d: 'M6.2 11.1c.9-1.1 1.7-1.1 2.2 0 .4.9 1 .9 1.4.2', width: 1.3 },
  ],
};

export const FALLBACK_MARK: MarkPart[] = [
  { kind: 'circle', cx: 8, cy: 8, r: 5.2, width: 1.3 },
  { kind: 'path', d: 'M8 5.4v5.2', width: 1.5 },
];

/**
 * The pipeline label a mounted stage shows under its node.
 *
 * The `Intelie` prefix is dropped so seven nodes fit without wrapping — every
 * pipeline product shares it, so it distinguishes nothing at this size, and the
 * full name is on the card directly below.
 */
export function mountLabel(productName: string): string {
  return productName.replace(/^Intelie\s*/i, '');
}

/** What each pipeline stage means, for the node's tooltip. */
export const STAGE_MEANING: Record<PipelineStage, string> = {
  Source: 'Where documents of this type arrive from',
  Ingest: 'Bringing them in on a schedule, without re-importing',
  Read: 'Turning the document into text',
  Redact: 'Masking personal data before anything travels downstream',
  Extract: 'Pulling out the fields worth having',
  Review: 'A human checking anything the model was unsure about',
  Deliver: 'Where the result ends up',
};
